import React, { useEffect, useState } from "react";
import PatientService from "../../../Services/PatientService";
import dayjs from "dayjs";
import { Modal, Button, Spin, message } from "antd";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ComposedChart,
  Legend,
  Scatter,
} from "recharts";

const RespiratoryChart = ({ smartWatchId }) => {
  const [tab, setTab] = useState("1D");
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [chartData1D, setChartData1D] = useState([]);
  const [chartData7D, setChartData7D] = useState([]);
  const [loading, setLoading] = useState(false);

  const normalize1DData = (apiData) => {
    const map = new Map(apiData.map((item) => [item.TIME, item]));

    return FULL_DAY_HOURS.map((hour) => ({
      ...hour,
      ...(map.get(hour.TIME) || {}),
    }));
  };

  const fetch1DData = async (date) => {
    if (!smartWatchId) return;
    const start = dayjs(date).format("DD/MM/YYYY");
    const payload = {
      type: "RESPIRATION",
      userId: smartWatchId,
      fromDate: start,
      toDate: start,
      offset: 0,
    };
    try {
      setLoading(true);
      const res = await PatientService.GarminRespirationDailyData(payload);
      if (res?.data?.length > 0) {
        const normalized = normalize1DData(res.data);
        setChartData1D(normalized);
      } else {
        setChartData1D(FULL_DAY_HOURS);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetch7DData = async (startDate, endDate) => {
    if (!smartWatchId) return;
    const start = dayjs(startDate).format("DD/MM/YYYY");
    const end = dayjs(endDate).format("DD/MM/YYYY");
    const payload = {
      type: "RESPIRATION",
      userId: smartWatchId,
      fromDate: start,
      toDate: end,
      offset: 0,
    };
    try {
      setLoading(true);
      const res = await PatientService.GarminRespirationWeeklyData(payload);
      if (res?.data?.length > 0) {
        const data = res.data.map((item) => {
          const date = item.DATE || dayjs().format("DD/MM/YYYY");
          return {
            ...item,
            date,
          };
        });
        setChartData7D(data);
      } else setChartData7D([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "1D") {
      fetch1DData(selectedDate);
    } else {
      const end = dayjs();
      const start = end.subtract(6, "day");
      fetch7DData(start, end);
    }
  }, [tab, selectedDate, smartWatchId]);

  const handlePrevDay = () => setSelectedDate(selectedDate.subtract(1, "day"));

  const handlePrev7D = () => {
    const start = dayjs(chartData7D[0]?.date, "DD/MM").subtract(7, "day");
    const end = dayjs(chartData7D[0]?.date, "DD/MM").subtract(1, "day");
    fetch7DData(start, end);
  };
  const handleNextDay = () => {
    const nextDay = selectedDate.add(1, "day");

    if (nextDay.isAfter(dayjs(), "day")) {
      message.info("Data for future dates will be available tomorrow.");
      return;
    }

    setSelectedDate(nextDay);
  };

  const handleNext7D = () => {
    if (!chartData7D.length) return;

    const lastDate = dayjs(chartData7D[chartData7D.length - 1]?.date, "DD/MM");

    let end = lastDate.add(7, "day");
    let start = end.subtract(6, "day");

    if (end.isAfter(dayjs(), "day")) {
      message.info("Data for future dates will be available tomorrow.");
      return;
    }

    fetch7DData(start, end);
  };

  const formatTime12H = (time, index) => {
    if (time === "24:00") return "12 AM";

    return dayjs(time, "HH:mm").format("h A");
  };

  const FULL_DAY_HOURS = Array.from({ length: 25 }, (_, i) => ({
    TIME: i === 24 ? "24:00" : dayjs(`${i}:00`, "H:mm").format("HH:mm"),
    MIN: null,
    MAX: null,
    AVG: null,
  }));

  return (
    <div>
      <div className="flex justify-center mb-4 gap-3">
        <button
          onClick={() => setTab("1D")}
          className={`${
            tab === "1D" ? "bg-brand-primaryColor text-white" : "bg-white"
          } px-4 py-2 rounded-md`}
        >
          1D
        </button>
        <button
          className={`${
            tab === "1D" ? "bg-white" : "bg-brand-primaryColor text-white"
          } px-4 py-2 rounded-md`}
          onClick={() => setTab("7D")}
        >
          7D
        </button>
      </div>

      {loading && <Loader />}

      {tab === "1D" && (
        <>
          <div className="flex items-center w-full justify-center gap-4 mb-4">
            <Button onClick={handlePrevDay}>← Prev Day</Button>
            <span className="font-semibold">
              {selectedDate.format("DD/MM/YYYY")}
            </span>
            <Button onClick={handleNextDay}>Next Day →</Button>
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%" margin="auto">
              <ComposedChart data={chartData1D}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="TIME"
                  type="category"
                  scale="point"
                  tickMargin={10}
                  tickFormatter={(time) => {
                    const hour = Number(time.split(":")[0]);
                    if (hour % 4 === 0) {
                      return formatTime12H(time);
                    }
                    return "";
                  }}
                />

                <YAxis />
                <Tooltip />
                <Legend />
                <Scatter dataKey="MIN" fill="#ef4444" />
                <Scatter dataKey="AVG" fill="#111827" />
                <Scatter dataKey="MAX" fill="#3b82f6" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {tab === "7D" && (
        <>
          <div className="flex justify-between mb-4">
            <button
              className="px-4 py-2 rounded-md text-white"
              onClick={handlePrev7D}
            >
              ← Prev 7 Days
            </button>
            {/* <span>
              {startDate.format("DD/MM/YYYY")} - {endDate.format("DD/MM/YYYY")}
            </span> */}
            <button
              className="px-4 py-2 rounded-md text-white"
              onClick={handleNext7D}
            >
              Next 7 Days →
            </button>
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData7D}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="SLEEPAVG"
                  stroke="#3b82f6"
                  strokeWidth={4}
                  dot
                />
                <Line
                  type="monotone"
                  dataKey="AWAKEAVG"
                  stroke="#ff8000"
                  strokeWidth={4}
                  dot
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

const Loader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm z-50">
    <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

export default RespiratoryChart;
